import type { Board, Notes } from './types';

export function emptyBoard(): Board {
  return Array.from({ length: 9 }, () => Array(9).fill(null));
}

export function emptyNotes(): Notes {
  return Array.from({ length: 9 }, () => Array.from({ length: 9 }, () => []));
}

export function formatTime(seconds: number): string {
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/**
 * Normal solo oyun için bulmaca başına hint bütçesi.
 * Zorluk seviyesi hint sayısını değiştirmez.
 */
export function getHintBudget(_difficulty: number): { free: number; rewarded: number } {
  return { free: 3, rewarded: 3 };
}

/** Puzzle hücresinin başlangıçta dolu olup olmadığını kontrol eder */
export function isGiven(puzzle: Board, r: number, c: number): boolean {
  return puzzle[r]?.[c] != null && puzzle[r]?.[c] !== 0;
}

/** Bir hücreye değer yerleştirilince aynı satır/sütun/kutu notlarını temizle */
export function clearAutoNotes(notes: Notes, r: number, c: number, value: number): Notes {
  const n = notes.map(row => row.map(cell => [...cell]));
  n[r][c] = [];
  for (let i = 0; i < 9; i++) {
    n[r][i] = n[r][i].filter(v => v !== value);
    n[i][c] = n[i][c].filter(v => v !== value);
  }
  const br = Math.floor(r / 3) * 3;
  const bc = Math.floor(c / 3) * 3;
  for (let rr = br; rr < br + 3; rr++)
    for (let cc = bc; cc < bc + 3; cc++)
      n[rr][cc] = n[rr][cc].filter(v => v !== value);
  return n;
}

/** Not toggle: değer varsa sil, yoksa ekle */
export function toggleNoteLocal(notes: Notes, r: number, c: number, value: number): Notes {
  const n = notes.map(row => row.map(cell => [...cell]));
  const idx = n[r][c].indexOf(value);
  if (idx >= 0) n[r][c].splice(idx, 1);
  else n[r][c].push(value);
  n[r][c].sort((a, b) => a - b);
  return n;
}

/** Bir board'un dolu olup olmadığını kontrol et (tüm hücreler dolu) */
export function isBoardComplete(board: Board): boolean {
  return board.every(row => row.every(cell => cell !== null && cell !== 0));
}

/** Sudoku constraint kontrolü: satır/sütun/kutu'da çakışma var mı? */
export function isConflict(board: Board, r: number, c: number, value: number): boolean {
  for (let i = 0; i < 9; i++) {
    if (i !== c && board[r][i] === value) return true;
    if (i !== r && board[i][c] === value) return true;
  }
  const br = Math.floor(r / 3) * 3;
  const bc = Math.floor(c / 3) * 3;
  for (let rr = br; rr < br + 3; rr++)
    for (let cc = bc; cc < bc + 3; cc++)
      if ((rr !== r || cc !== c) && board[rr][cc] === value) return true;
  return false;
}

// ═══════════════════════════════════════════════════════════
// Offline local-authoritative motor — solution zaten indirilmiş olduğu için
// (offline paket sunucu tarafından imzalanır, bkz. backend/main.py
// _issue_offline_ticket) hamleler sunucuya sormadan burada doğrulanır.
// Online modda bu fonksiyonlar KULLANILMAZ — orada sunucu otorite kalır.
// ═══════════════════════════════════════════════════════════

export interface PlaceLocalResult {
  board: Board;
  notes: Notes;
  /** Hamle given/dolu bir hücreye yapıldıysa (geçersiz istek) true. */
  invalid: boolean;
  correct: boolean;
  /** Yalnızca `correct` true iken anlamlı: tahta artık tamamen dolu mu? */
  isComplete: boolean;
}

/**
 * Bir hücreye değer yerleştirmenin saf/lokal karşılığı (online `/game/place`'in
 * offline eşdeğeri). `puzzle` given-hücre korumasını netlik/test edilebilirlik
 * için burada da tekrar kontrol eder — çağıran taraf zaten `isGiven` ile
 * ön-kontrol yapar (bkz. GamePage.tsx/game.tsx), bu ikinci kontrol savunma amaçlıdır.
 */
export function placeLocal(
  puzzle: Board,
  board: Board,
  notes: Notes,
  solution: number[][],
  r: number,
  c: number,
  value: number,
  autoCleanNotes: boolean,
): PlaceLocalResult {
  if (isGiven(puzzle, r, c) || board[r][c] !== null) {
    return { board, notes, invalid: true, correct: false, isComplete: false };
  }
  const correct = solution[r]?.[c] === value;
  if (!correct) {
    return { board, notes, invalid: false, correct: false, isComplete: false };
  }
  const newBoard = board.map((row, rr) => row.map((cell, cc) => (rr === r && cc === c ? value : cell)));
  const newNotes = autoCleanNotes
    ? clearAutoNotes(notes, r, c, value)
    : notes.map((row, rr) => row.map((cell, cc) => (rr === r && cc === c ? [] : [...cell])));
  return { board: newBoard, notes: newNotes, invalid: false, correct: true, isComplete: isBoardComplete(newBoard) };
}

/** Bir hücreyi silmenin saf/lokal karşılığı. Given hücrede no-op (board aynen döner). */
export function eraseLocal(puzzle: Board, board: Board, r: number, c: number): Board {
  if (isGiven(puzzle, r, c)) return board;
  return board.map((row, rr) => row.map((cell, cc) => (rr === r && cc === c ? null : cell)));
}

export interface HintLocalResult {
  board: Board;
  row: number;
  col: number;
  value: number;
  /** Bu ipucu tahtadaki SON boş hücreyi doldurduysa true — çağıran taraf
   * bu durumda placeLocal'daki tamamlanma yoluyla aynı finalize akışını tetiklemeli. */
  isComplete: boolean;
}

/** İlk boş hücreyi solution'dan doldurur (düz ipucu — adım adım açıklama offline'da yok). */
export function hintLocal(board: Board, solution: number[][]): HintLocalResult | null {
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      if (board[r][c] === null) {
        const value = solution[r][c];
        const newBoard = board.map((row, rr) => row.map((cell, cc) => (rr === r && cc === c ? value : cell)));
        return { board: newBoard, row: r, col: c, value, isComplete: isBoardComplete(newBoard) };
      }
    }
  }
  return null; // tahta zaten tamamen dolu
}
